/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { IDBPDatabase } from "idb";

import type { IStorageAdapter } from "../interfaces/IStorageAdapter";
import { Log } from "../logging/Log";
import { computeChecksum, verifyChecksum } from "./checksumUtils";
import { STORE_NAME, isChecksummed, EVICT_TILL_INDEX } from "./storageConfig";
import { openStorageDB } from "./storageDatabase";
import type { TStorageRecord } from "./storageDatabase";

/**
 * Client-side storage adapter backed by IndexedDB.
 *
 * This is the **single** point of access for all client-side storage.
 * Application code MUST use this adapter — never raw `indexedDB` calls,
 * `localStorage`, `sessionStorage`, or in-memory module-level caches.
 *
 * **Instantiation:** Use the async factory {@link StorageAdapter.create} —
 * the constructor is private because opening the database is asynchronous.
 *
 * **UAW-66 scope:** Constructor + static factory only. Method implementations
 * are delivered in UAW-67 (read/write) and UAW-68 (cleanup/clear).
 */
export class StorageAdapter implements IStorageAdapter {
  private static readonly TAG: string = "StorageAdapter";

  /**
   * Singleton instance. Set by {@link init}, accessed via {@link getInstance}.
   */
  private static mInstance: StorageAdapter | null = null;

  /**
   * The resolved IndexedDB database handle.
   * Guaranteed to be open and schema-ready after {@link init} resolves.
   */
  private readonly mDb: IDBPDatabase;

  /**
   * Private constructor — use {@link StorageAdapter.init} instead.
   *
   * @param db - A resolved IDBPDatabase handle (already opened with the correct schema)
   */
  private constructor(db: IDBPDatabase) {
    this.mDb = db;
  }

  /**
   * Initialise the singleton StorageAdapter. Opens the IndexedDB database,
   * creates the schema if needed, and stores the instance for later access
   * via {@link getInstance}.
   *
   * Must be called once at app boot, before React renders.
   *
   * @returns The initialised StorageAdapter singleton
   * @throws Re-throws any error from {@link openStorageDB} after logging
   */
  public static async init(): Promise<StorageAdapter> {
    if (StorageAdapter.mInstance !== null) {
      Log.w(
        StorageAdapter.TAG,
        "init(): Already initialised — returning existing instance"
      );
      return StorageAdapter.mInstance;
    }

    try {
      Log.d(StorageAdapter.TAG, "init(): Initialising storage adapter");
      const db: IDBPDatabase = await openStorageDB();
      const adapter: StorageAdapter = new StorageAdapter(db);
      await adapter.cleanupExpired();
      StorageAdapter.mInstance = adapter; // assign only on full success
      Log.i(
        StorageAdapter.TAG,
        "init(): Storage adapter initialised successfully"
      );
      return StorageAdapter.mInstance;
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "init()", error);
      throw error;
    }
  }

  /**
   * Get the live singleton instance.
   *
   * @returns The StorageAdapter singleton
   * @throws Error if called before {@link init} has completed
   */
  public static getInstance(): StorageAdapter {
    if (StorageAdapter.mInstance === null) {
      throw new Error(
        "StorageAdapter not initialised — call StorageAdapter.init() first"
      );
    }
    return StorageAdapter.mInstance;
  }

  /**
   * Close the database connection and release the singleton.
   * Called during logout or when storage needs to be re-initialised.
   */
  public static destroy(): void {
    if (StorageAdapter.mInstance !== null) {
      StorageAdapter.mInstance.mDb.close();
      StorageAdapter.mInstance = null;
      Log.i(StorageAdapter.TAG, "destroy(): Storage adapter destroyed");
    }
  }

  /**
   * Read a cached record by key.
   *
   * Validation order (per StorageGuidelines §6):
   * 1. Record not found → return null (cache miss)
   * 2. TTL expired → delete record → return null
   * 3. Checksummed key → verify SHA-256 → mismatch → delete → return null
   * 4. Return record.data
   *
   * @param key - The cache key to read
   * @returns The cached data payload, or null on cache miss
   */
  public async get<T>(key: string): Promise<T | null> {
    try {
      const record: TStorageRecord | undefined = await this.mDb.get(
        STORE_NAME,
        key
      );

      if (record === undefined || record === null) {
        Log.d(
          StorageAdapter.TAG,
          `get("${key}"): Cache miss — record not found`
        );
        return null;
      }

      if (Date.now() > record.validTill) {
        Log.d(
          StorageAdapter.TAG,
          `get("${key}"): TTL expired — deleting record`
        );
        await this.mDeleteRecord(key);
        return null;
      }

      if (isChecksummed(key)) {
        if (record.checksum === null || record.checksum === undefined) {
          Log.w(
            StorageAdapter.TAG,
            `get("${key}"): Checksummed key missing checksum — deleting record`
          );
          await this.mDeleteRecord(key);
          return null;
        }

        const isValid: boolean = await verifyChecksum(
          record.data,
          record.checksum
        );

        if (!isValid) {
          Log.w(
            StorageAdapter.TAG,
            `get("${key}"): Checksum mismatch — deleting record`
          );
          await this.mDeleteRecord(key);
          return null;
        }
      }

      Log.d(StorageAdapter.TAG, `get("${key}"): Cache hit`);
      return record.data as T;
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "get()", error);
      throw error;
    }
  }

  /**
   * Write a record to the cache.
   *
   * Computes `validTill = Date.now() + ttl`. For checksummed keys,
   * a SHA-256 checksum of `JSON.stringify(data)` is stored alongside.
   *
   * @param key - The cache key
   * @param data - The data payload to cache
   * @param ttl - Time to live in milliseconds (defaults to 24 hours)
   */
  public async set<T>(
    key: string,
    data: T,
    ttl: number = 86_400_000, // 24 hours
    evictTill: number = Infinity // Optional eviction timestamp for cleanup
  ): Promise<void> {
    try {
      const validTill: number = Date.now() + ttl;
      let checksum: string | null = null;

      if (isChecksummed(key)) {
        checksum = await computeChecksum(data);
      }

      const record: TStorageRecord = {
        key,
        data,
        checksum,
        validTill,
        evictTill
      };

      await this.mDb.put(STORE_NAME, record);
      Log.d(
        StorageAdapter.TAG,
        `set("${key}"): Record written (validTill=${validTill})`
      );
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "set()", error);
      throw error;
    }
  }

  /**
   * Internal delete — performs the raw IndexedDB delete without its
   * own try/catch so that callers like {@link get} and {@link has}
   * log the error exactly once in their own catch block.
   *
   * @param key - The cache key to remove
   */
  private async mDeleteRecord(key: string): Promise<void> {
    await this.mDb.delete(STORE_NAME, key);
  }

  /**
   * Delete a single cached record by key.
   *
   * @param key - The cache key to remove
   */
  public async delete(key: string): Promise<void> {
    try {
      await this.mDeleteRecord(key);
      Log.d(StorageAdapter.TAG, `delete("${key}"): Record deleted`);
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "delete()", error);
      throw error;
    }
  }

  /**
   * Check whether a valid (non-expired) record exists for the given key.
   *
   * Performs a TTL check but does NOT verify the checksum — use
   * {@link get} when the data itself is needed with full integrity
   * validation.
   *
   * @param key - The cache key to check
   * @returns true if a non-expired record exists; false otherwise
   */
  public async has(key: string): Promise<boolean> {
    try {
      const record: TStorageRecord | undefined = await this.mDb.get(
        STORE_NAME,
        key
      );

      if (record === undefined || record === null) {
        return false;
      }

      if (Date.now() > record.validTill) {
        await this.mDeleteRecord(key);
        return false;
      }

      return true;
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "has()", error);
      throw error;
    }
  }

  /**
   * List every record key that begins with `prefix`, expired records included.
   *
   * Needed by stores whose keys carry a dynamic suffix — the per-context token
   * records `profileDomainToken:<bodyId>:<contextId>`, for instance, cannot be
   * enumerated from constants alone, so clearing them all on sign-out requires
   * asking the database which ones exist.
   *
   * @param prefix - Key prefix to match
   * @returns The matching keys; empty when none match
   */
  public async keysWithPrefix(prefix: string): Promise<string[]> {
    try {
      // A key range is an index scan rather than a full-store read: every key
      // sorts as a string, so the prefix bound stops at the first non-match.
      const range: IDBKeyRange = IDBKeyRange.bound(
        prefix,
        prefix + "￿",
        false,
        false
      );
      const keys: IDBValidKey[] = await this.mDb.getAllKeys(STORE_NAME, range);
      return keys.map((key: IDBValidKey): string => String(key));
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "keysWithPrefix()", error);
      throw error;
    }
  }

  /**
   * Clear all records from the database. Called on user logout.
   */
  public async clear(): Promise<void> {
    try {
      Log.d(StorageAdapter.TAG, "clear(): Clearing all cached records");
      await this.mDb.clear(STORE_NAME);
      Log.i(StorageAdapter.TAG, "clear(): All cached records cleared");
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "clear()", error);
      throw error;
    }
  }

  /**
   * Delete all expired records from the database. Called at startup.
   */
  private async cleanupExpired(): Promise<void> {
    try {
      Log.d(
        StorageAdapter.TAG,
        "cleanupExpired(): Starting expired record cleanup"
      );
      const now: number = Date.now();

      // Get today's start-of-day timestamp for EPG date comparison
      const today: Date = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp: number = today.getTime();

      const transaction = this.mDb.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      // First pass: delete records expired by TTL using the evictTill index
      const evictTillIndex = store.index(EVICT_TILL_INDEX);
      const expiredByTtlKeys: Array<IDBValidKey> =
        await evictTillIndex.getAllKeys(IDBKeyRange.upperBound(now));
      for (const key of expiredByTtlKeys) {
        store.delete(key);
      }

      // Second pass: EPG-specific cleanup based on date encoded in the key
      // Limit to keys starting with "epg_" using a bounded key range
      const epgKeyRange: IDBKeyRange = IDBKeyRange.bound("epg_", "epg_\uffff");
      const epgKeys: Array<IDBValidKey> = await store.getAllKeys(epgKeyRange);
      for (const key of epgKeys) {
        if (typeof key !== "string") {
          continue;
        }
        const epgDateTimestamp: number | null = this.parseEpgKeyDate(key);
        if (epgDateTimestamp !== null && epgDateTimestamp < todayTimestamp) {
          store.delete(key);
        }
      }

      await transaction.done;
    } catch (error: unknown) {
      Log.e(StorageAdapter.TAG, "cleanupExpired()", error);
      throw error;
    }
  }

  /**
   * Parse an EPG key to extract the date timestamp.
   *
   * @param key - EPG key in format "epg_YYYY_MM_DD"
   * @returns Timestamp for start of that day (local timezone), or null if invalid format
   */
  private parseEpgKeyDate(key: string): number | null {
    const match: RegExpMatchArray | null = key.match(
      /^epg_(\d{4})_(\d{2})_(\d{2})$/
    );
    if (match === null) {
      return null;
    }

    const year: number = parseInt(match[1], 10);
    const month: number = parseInt(match[2], 10);
    const day: number = parseInt(match[3], 10);

    const date: Date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  /**
   * Get the underlying database handle.
   * Exposed for testing and for UAW-67/68 implementations that need direct store access.
   *
   * @returns The IDBPDatabase instance
   */
  public getDb(): IDBPDatabase {
    return this.mDb;
  }

  /**
   * String representation for debugging.
   *
   * @returns Debug string identifying this adapter instance
   */
  public toString(): string {
    return `StorageAdapter(db=${this.mDb.name}, version=${this.mDb.version})`;
  }
}
